import { Button, Flex, Label } from "@aws-amplify/ui-react"
import {
  AnimalGroupCreateSchema,
  AnimalGroupSchemaNameMaxLength,
  AnimalGroupSchemaNumberOfAnimalsMax,
  AnimalGroupUpdateSchema,
} from "@strtmn/sds/schemas"
import type {
  AnimalGroupCreateDto,
  AnimalGroupDto,
  AnimalGroupUpdateDto,
  OrganizationDto,
  ResponseMessage,
} from "@strtmn/sds/types"
import { del, post, put } from "aws-amplify/api"
import { Cache, I18n } from "aws-amplify/utils"
import { useContext, useState } from "react"
import { FormProvider, useForm, type SubmitHandler } from "react-hook-form"
// import { FaRegTrashCan } from "react-icons/fa6"
import { useNavigate, useParams } from "react-router-dom"
import { CancelButton, Modal } from "../../../components"
import config from "../../../config"
import { UserDataContext, UserDataContextHelper } from "../../../contexts"
import { useIsReadOnly } from "../../../hooks"
import { getLinkToOrgAnimalGroups } from "../../../LinkUtils"
import { currentSession } from "../../../Utils.AmplifyAuth"
import { imageHash } from "../-components/ListItem"

import "../RecipeManagement.css"

const NUM_AG_IMAGES = config.images.ag.numImages
const IMAGE_HASH_SEED = config.images.ag.imageHashSeed

type ComponentModes = "create" | "edit" | "invalid"

/** Cleans the form data before submission. */
export function cleanData(
  data: AnimalGroupCreateDto,
  isCreateMode: boolean
): AnimalGroupCreateDto | AnimalGroupUpdateDto {
  console.debug("▶️ cleanData")
  /** @ts-ignore - user input can be string */
  if ("" === data.number_of_animals) {
    data.number_of_animals = 0
  }
  let cleaned
  if (isCreateMode) {
    cleaned = AnimalGroupCreateSchema.cast(data)
    delete cleaned.id
  } else {
    cleaned = AnimalGroupUpdateSchema.cast(data)
  }
  return cleaned
}

const getEmptyAnimalGroup = (org: OrganizationDto): AnimalGroupDto => {
  return {
    id: "",
    org_id: org.id,
    name: "",
    number_of_animals: 10,
    querband_geschwindigkeit: 0,
    created_at: "",
    created_by: "",
    updated_at: "",
    updated_by: "",
  }
}

export default function AnimalGroupCreateEdit() {
  const params = useParams()
  const navigate = useNavigate()
  const { userData, reloadUserDataAsync } = useContext(UserDataContext)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)

  const isReadOnly = useIsReadOnly()
  const isCreateMode = window.location.pathname.endsWith("/new")
  const isEditMode =
    window.location.pathname.endsWith("/edit") &&
    params["orgId"] &&
    params["animalGroupId"]
  const componentMode: ComponentModes = isCreateMode
    ? "create"
    : isEditMode
      ? "edit"
      : "invalid"
  const modalCallback = async (value: string) => {
    console.log("> modalCallback:", value)
    if (value.trim().toLowerCase() === "ja") {
      console.log("wanna delete: " + animalGroup.id)
      await deleteAnimalGroup(animalGroup.id)
      /** @todo set global success message with data from `animalGroup` */

      console.log("Invalidating Cache")
      const cacheKey = "frontendApi#/users/current"
      await Cache.removeItem(cacheKey)

      if (reloadUserDataAsync) {
        console.log("Reloading data")
        await reloadUserDataAsync()
      } else {
        console.warn("Not reloading data; callback not set")
      }

      console.log("Navigating back")
      navigate(getLinkToOrgAnimalGroups(org))
    } else {
      console.log("NO wanna delete")
    }
    setShowArchiveModal(false)
  }

  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)
  const animalGroup: AnimalGroupDto = isEditMode
    ? UserDataContextHelper.findAnimalGroup(org, params["animalGroupId"]!)
    : getEmptyAnimalGroup(org)

  const methods = useForm<AnimalGroupCreateDto>({
    values: animalGroup,
    mode: "onBlur",
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = methods

  if (componentMode === "invalid") {
    return <div>Errrr... invalid component mode</div>
  }

  const onSubmit: SubmitHandler<AnimalGroupCreateDto> = async (data) => {
    console.log("▶️ onSubmit")
    if (isReadOnly) {
      console.log("🚧 Read-only mode.")
      setError("root.clientError", {
        type: "ui.read-only",
        message: "Bearbeiten ist nicht erlaubt.",
      })
      return
    }
    data.org_id = org.id
    setIsFormSubmitting(true)
    const cleaned = cleanData(data, isCreateMode)
    // const clean = data
    // console.log('clean', clean)
    // console.log("\n"
    //   + `${clean.name}\n`
    //   + `  Komponenten\n`
    //   + clean.RecipeComponents.map(rco => {
    //     const fcId = rco.feed_component_id
    //     const fc = AppContextHelper.findAnimalGroup(org, fcId)
    //     return `    ${fc.name}\t${rco.mass_kg} kg\t${rco.mixing_time_s} s`
    //   }).join("\n")
    //   + "\n"
    //   + `  Ausladegruppen\n`
    //   + clean.RecipesAusladegruppen.map(rag => {
    //     const agId = rag.ausladegruppe_id
    //     const ag = AppContextHelper.findAusladegruppe(org, agId)
    //     return `    ${ag.name}\t${ag.number_of_animals} T`
    //   }).join("\n")
    // )
    /** @todo Implement proper client-side validation (different for 'create' and 'update') */
    if (!AnimalGroupCreateSchema.isValidSync(cleaned)) {
      console.warn("❌ Cleaned data is not valid")
      try {
        await AnimalGroupCreateSchema.validate(cleaned, { abortEarly: false })
      } catch (err) {
        /** @ts-ignore */
        console.log("Validation errors (cleaned)", err.errors)
        const response: ResponseMessage = {
          res: {
            code: "400",
            /** @ts-ignore */
            message: `${err.errors.length} Errors. The first one is: ${err.errors[0]}`,
          },
          sessionId: "",
        }
        setIsFormSubmitting(false)
        return response
      }
    } else {
      console.log("✅ Cleaned data is valid")
    }
    const result = isEditMode
      ? await updateAnimalGroup(animalGroup.id, cleaned as AnimalGroupUpdateDto)
      : await createAnimalGroup(cleaned as AnimalGroupCreateDto)
    /** @todo Result is meaningless? */
    /** @todo Error handling */

    console.log("Invalidating Cache")
    const cacheKey = "frontendApi#/users/current"
    await Cache.removeItem(cacheKey)

    if (reloadUserDataAsync) {
      console.log("Reloading data")
      await reloadUserDataAsync()
    } else {
      console.warn("Not reloading data; callback not set")
    }

    setIsFormSubmitting(false)
    console.log("Navigating back")
    navigate(getLinkToOrgAnimalGroups(org))

    return result
  }

  const prepareApiCall = async (path: string, body: any = false) => {
    console.debug("▶️ prepareApiCall")
    const tokens = await currentSession()
    const apiName = "frontendApi"
    const options = {
      headers: {
        Authorization: `Bearer ${tokens?.idToken}`,
      },
      body: body,
    }
    if (!body) {
      delete options.body
    }
    console.info(`Prepared API call to ${path}`)
    return { apiName, path, options }
  }
  async function createAnimalGroup(data: AnimalGroupCreateDto) {
    console.log("▶️ createAnimalGroup")
    const apiCallParams = await prepareApiCall(`/animal-groups`, data)
    const restOperation = post(apiCallParams)
    const apiResponse = (await (
      await restOperation.response
    ).body.json()) as unknown as ResponseMessage
    console.log("Got API response:", apiResponse)
    console.log("Got restOperation.response:", restOperation.response)
    return apiResponse
  }
  async function updateAnimalGroup(
    animalGroupId: string,
    data: AnimalGroupUpdateDto
  ): Promise<ResponseMessage> {
    // -> PUT
    console.log("▶️ updateAnimalGroup")
    const apiCallParams = await prepareApiCall(
      `/animal-groups/${animalGroupId}`,
      data
    )
    const restOperation = put(apiCallParams)
    const apiResponse = (await (
      await restOperation.response
    ).body.json()) as unknown as ResponseMessage
    console.log("Got API response:", apiResponse)
    console.log("Got restOperation.response:", restOperation.response)
    return apiResponse
  }
  async function deleteAnimalGroup(animalGroupId: string) {
    console.log("▶️ deleteAnimalGroup", animalGroupId)
    const apiCallParams = await prepareApiCall(
      `/animal-groups/${animalGroupId}`
    )
    const restOperation = del(apiCallParams)
    const statusCode: number = (await restOperation.response).statusCode
    console.log("Got API response with statusCode:", statusCode)
    console.log("Got restOperation.response:", restOperation.response)
    return statusCode
  }

  const imageIdx = imageHash(IMAGE_HASH_SEED, animalGroup.id, NUM_AG_IMAGES)
  const imageSrc = `/images/ag/animals-pixabay-${imageIdx}.jpg`

  return (
    <>
      <div className="rm-edit-page">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${imageSrc}')` }}
        />
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              <h2>{animalGroup.name}</h2>
            </div>
          </div>

          <div className="details">
            {/* pass all methods into the context */}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Flex direction="column" gap="small">
                  <Flex direction="row" gap="small" className="field-row">
                    <Label htmlFor="name">Name der Ausladegruppe:</Label>
                    <input
                      {...register("name", {
                        required: "Geben Sie den Namen der Ausladegruppe an.",
                        maxLength: {
                          value: AnimalGroupSchemaNameMaxLength,
                          message: `Der Name darf max. ${AnimalGroupSchemaNameMaxLength} Zeichen lang sein.`,
                        },
                      })}
                      placeholder={animalGroup.name || "Neue Ausladegruppe"}
                      className={errors?.name ? "error" : ""}
                    />
                    {errors.name && <div>Fehler: {errors?.name.message}</div>}
                  </Flex>

                  <Flex direction="row" gap="small" className="field-row">
                    <Label htmlFor="number_of_animals">Anzahl Tiere:</Label>
                    <input
                      {...register("number_of_animals", {
                        required: "Geben Sie die Anzahl der Tiere an.",
                        min: {
                          value: 1,
                          message:
                            "Die Anzahl der Tiere muss mindestens 1 sein.",
                        },
                        max: {
                          value: AnimalGroupSchemaNumberOfAnimalsMax,
                          message: `Die Anzahl der Tiere darf maximal ${AnimalGroupSchemaNumberOfAnimalsMax} sein.`,
                        },
                      })}
                      className={errors?.number_of_animals ? "error" : ""}
                    />
                  </Flex>
                  {errors.number_of_animals && (
                    <div className="error">
                      Fehler: {errors.number_of_animals.message}
                    </div>
                  )}

                  <Flex
                    direction="row"
                    gap="small"
                    alignItems="center"
                    className="field-row"
                  >
                    <Label htmlFor="querband_geschwindigkeit">
                      Querband Geschwindigkeit *:
                    </Label>
                    <Flex alignItems="center" gap="small">
                      <input
                        {...register("querband_geschwindigkeit", {
                          required: false,
                          min: {
                            value: 0,
                            message:
                              "Die Querband-Geschwindigkeit muss mindestens 0 % sein.",
                          },
                          max: {
                            value: 100,
                            message:
                              "Die Querband-Geschwindigkeit darf maximal 100 % sein.",
                          },
                        })}
                        className={
                          errors?.querband_geschwindigkeit ? "error" : ""
                        }
                      />
                      %
                    </Flex>
                  </Flex>
                  {errors.querband_geschwindigkeit && (
                    <div className="error">
                      Fehler: {errors.querband_geschwindigkeit.message}
                    </div>
                  )}
                  <Flex>
                    * Eine Bandgeschwindigkeit von 0 % bedeutet, dass die
                    Geschwindigkeit über den Regler in der Armlehne geregelt
                    wird.
                  </Flex>

                  {errors?.root && errors?.root["clientError"] && (
                    <p className="error">
                      {errors.root["clientError"].message}
                    </p>
                  )}
                  {errors?.root && errors?.root["serverError"] && (
                    <p className="error">
                      {errors.root["serverError"].message}
                    </p>
                  )}

                  <div className="buttons">
                    <Button
                      type="submit"
                      variation="primary"
                      isDisabled={isFormSubmitting || isReadOnly}
                      isLoading={isFormSubmitting}
                      loadingText={I18n.get("Wird gespeichert...")}
                    >
                      Speichern
                    </Button>
                    <CancelButton
                      href=".."
                      title="Abbrechen"
                      isDisabled={isFormSubmitting}
                    />
                    {componentMode === "edit" && (
                      <Button
                        isDisabled={isFormSubmitting || isReadOnly}
                        variation="warning"
                        onClick={() => {
                          setShowArchiveModal(true)
                        }}
                      >
                        Archivieren
                      </Button>
                    )}
                    {componentMode === "edit" && showArchiveModal && (
                      <Modal
                        buttons={[
                          {
                            text: "Ja",
                            variation: "destructive",
                          },
                          { text: "Nein" },
                        ]}
                        show={showArchiveModal}
                        callback={modalCallback}
                      >
                        <div className="title">
                          Ausladegruppe wirklich archivieren?
                        </div>
                        <div>
                          Wollen Sie die Ausladegruppe "{animalGroup.name}"
                          wirklich archivieren?
                        </div>
                        <div>
                          Wenn ein Eintrag archiviert ist, kann er nicht mehr
                          bearbeitet werden, und wird nicht mehr zu den
                          Maschinen übertragen.
                          <br />
                          In Statistiken taucht er weiterhin auf.
                        </div>
                      </Modal>
                    )}
                  </div>
                </Flex>
              </form>
            </FormProvider>

            {/* {componentMode === "edit"
            &&
            <div className="mt-5 text-xs">
              <div>Zuletzt bearbeitet am {animalGroup.updated_at} von {animalGroup.updated_by}</div>
              <div>Angelegt am {animalGroup.created_at} von {animalGroup.created_by}</div>
            </div>
          } */}
          </div>
        </div>
      </div>

      {/* <small>* Die Icons "xmark (light)", "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}
